/**
 * Custom hook for managing event stock data
 */

import { useState, useEffect } from 'react';
import { useRecordContext } from 'react-admin';
import { db } from '../../../database/firebase';
import { collection, getDocs, query, where } from 'firebase/firestore';
import {
  calculateProductSales,
  calculateRemainingPayments,
  calculateCouponsData,
  calculateCommission,
  calculateActualSalesData,
} from '../../../utils/eventStockCalculations';

export const useEventStockData = () => {
  const record = useRecordContext();
  const event = record;

  const [isLoading, setIsLoading] = useState(true);
  const [data, setData] = useState([]);
  const [remainingData, setRemainingData] = useState([]);
  const [couponsData, setCouponsData] = useState([]);
  const [actualSalesData, setActualSalesData] = useState({});
  const [totalProductSalesSummary, setTotalProductSalesSummary] = useState(0);
  const [totalRemainingAmountSummary, setTotalRemainingAmountSummary] = useState(0);
  const [totalCouponDiscountSummary, setTotalCouponDiscountSummary] = useState(0);
  const [subTotal, setSubTotal] = useState(0);
  const [totalCommissionAmountSummary, setTotalCommissionAmountSummary] = useState(0);
  const [check, setCheck] = useState(false);
  const [isApproved, setIsApproved] = useState(false);
  const [isPaid, setIsPaid] = useState(false);

  let hasEnteredFetching = false;

  useEffect(() => {
    if (!event || !event.id) return;

    const fetchData = async () => {
      setIsLoading(true);
      hasEnteredFetching = false;

      try {
        // Fetch sales data
        const salesData = await fetchEventSalesData();
        if (!salesData) return;

        // Calculate product sales
        const productSales = calculateProductSales(event.stock, salesData);
        setData(productSales.grid);
        setTotalProductSalesSummary(productSales.grid.find(p => p.id === 'total')?.totalSale || 0);

        // Calculate remaining payments
        const remaining = calculateRemainingPayments(event.stock, salesData);
        setRemainingData(remaining);
        setTotalRemainingAmountSummary(remaining.find(p => p.id === 'total')?.totalRemainingAmount || 0);

        // Calculate coupons data
        const coupons = calculateCouponsData(event);
        setCouponsData(coupons);
        setTotalCouponDiscountSummary(coupons.find(c => c.id === 'total')?.totalDiscount || 0);

        // Calculate commission
        const commissionData = calculateCommission(
          event,
          productSales.grid.find(p => p.id === 'total')?.totalSale || 0,
          salesData.totalSaleAmount,
          salesData.totalAmountOfCashSales,
          coupons.find(c => c.id === 'total')?.totalDiscount || 0,
          remaining.find(p => p.id === 'total')?.totalRemainingAmount || 0
        );

        setSubTotal(commissionData.subTotal);
        setTotalCommissionAmountSummary(commissionData.commission);
        setCheck(commissionData.checkCalc);

        // Set payment status
        setIsApproved(event.isApproved);
        setIsPaid(event.isPaid);

        setIsLoading(false);
      } catch (error) {
        console.error('Error fetching event stock data:', error);
        setIsLoading(false);
      }
    };

    fetchData();
  }, [event]);

  const fetchEventSalesData = async () => {
    if (!event || !event.id) return null;
    if (hasEnteredFetching) return null;
    hasEnteredFetching = true;

    try {
      const q = query(
        collection(db, 'sales'),
        where('event.id', '==', event.id),
        where('status', '==', 1),
      );

      const querySnapshot = await getDocs(q);
      const salesDocs = querySnapshot.docs;
      const actualSalesData = calculateActualSalesData(salesDocs);
      
      setActualSalesData(actualSalesData);
      return { salesDocs, ...actualSalesData };
    } catch (error) {
      console.error('Error fetching sales data:', error);
      return null;
    }
  };

  return {
    isLoading,
    data,
    remainingData,
    couponsData,
    actualSalesData,
    totalProductSalesSummary,
    totalRemainingAmountSummary,
    totalCouponDiscountSummary,
    subTotal,
    totalCommissionAmountSummary,
    check,
    isApproved,
    isPaid,
    setIsApproved,
    setIsPaid,
  };
};
